import { useEffect, useState, type FormEvent } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";

const navItems = [
  { label: "首页", to: "/" },
  { label: "篮球", to: "/category/basketball" },
  { label: "足球", to: "/category/football" },
  { label: "电竞", to: "/category/esports" },
  { label: "赛程", to: "/schedule" },
  { label: "关于我们", to: "/about" },
  { label: "联系我们", to: "/contact" },
];

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const handleSearch = (e: FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    if (!q) return;
    navigate(`/search?q=${encodeURIComponent(q)}`);
    setMobileOpen(false);
  };

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    `relative rounded-md px-3 py-2 text-sm font-medium transition-colors whitespace-nowrap ${
      isActive
        ? "text-primary-400"
        : "text-foreground-600 hover:text-foreground-950"
    }`;

  return (
    <header className="sticky top-0 z-50 border-b border-background-200 bg-background-50/90 backdrop-blur">
      <div className="mx-auto flex h-16 items-center gap-4 px-4 md:px-6">
        <Link
          to="/"
          className="flex items-center gap-2.5 shrink-0 cursor-pointer"
          aria-label="jrs直播 首页"
        >
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 font-heading text-lg font-extrabold text-background-50">
            J
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-heading text-base font-extrabold tracking-tight text-foreground-950">
              jrs直播
            </span>
            <span className="text-[10px] font-medium uppercase tracking-[0.18em] text-foreground-500">
              Live Sports
            </span>
          </span>
        </Link>

        <nav className="ml-4 hidden items-center gap-1 lg:flex">
          {navItems.map((item) => (
            <NavLink key={item.to} to={item.to} end={item.to === "/"} className={linkClass}>
              {item.label}
            </NavLink>
          ))}
        </nav>

        <form
          onSubmit={handleSearch}
          className="ml-auto hidden md:flex items-center gap-2 rounded-full border border-background-300 bg-background-100 px-3.5 py-2 w-60 lg:w-72"
          role="search"
        >
          <i className="ri-search-line text-base text-foreground-500" />
          <input
            type="text"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="搜索赛事名称或球队"
            className="w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-500 focus:outline-none"
            aria-label="搜索赛事名称或球队"
          />
        </form>

        <button
          type="button"
          onClick={() => setMobileOpen((v) => !v)}
          className="ml-auto flex h-10 w-10 items-center justify-center rounded-lg border border-background-300 bg-background-100 text-foreground-800 lg:hidden cursor-pointer"
          aria-label="打开导航菜单"
          aria-expanded={mobileOpen}
        >
          <i className={mobileOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"} />
        </button>
      </div>

      {mobileOpen && (
        <div className="border-t border-background-200 bg-background-50 lg:hidden">
          <div className="px-4 py-4 md:px-6">
            <form
              onSubmit={handleSearch}
              className="mb-4 flex items-center gap-2 rounded-full border border-background-300 bg-background-100 px-3.5 py-2.5"
              role="search"
            >
              <i className="ri-search-line text-base text-foreground-500" />
              <input
                type="text"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索赛事名称或球队"
                className="w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-500 focus:outline-none"
                aria-label="搜索赛事名称或球队"
              />
            </form>
            <nav className="grid grid-cols-2 gap-2">
              {navItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === "/"}
                  className={({ isActive }) =>
                    `rounded-lg border px-3 py-2.5 text-sm font-medium whitespace-nowrap ${
                      isActive
                        ? "border-primary-500/50 bg-primary-500/10 text-primary-400"
                        : "border-background-200 bg-background-100 text-foreground-700"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
            </nav>
          </div>
        </div>
      )}
    </header>
  );
}